import {
  conditionDisclaimers,
  getDisclaimerAnchor,
  getDisclaimerAnchorLinkText,
  getDisclaimerDescriptionMarkup,
  getDisclaimerTypeByCondition,
} from './conditionDisclaimers';
import {
  getLocalCopy,
  getLocalHealthCopiesByBreed,
  isLocalCopyAvailable,
} from './localHealthRepo';

import { MYEV_API_BASE } from '../config';
import { notifyHandledException } from '../utils/errorLogger';
import { pushGTMCustomEvent } from './pushGTMCustomEvent';

/**
 * Breed Disease & Health Search
 *
 * @since 2018-06-24
 * @see sections/product-template.liquid
 *
 */
export async function initBreedSpecificDiseaseSelector() {
  /**
   * Frequently used selectors.
   *
   * Uses native JavaScript document query methods to find elements
   * because they are much faster than jQuery. Returned elements are
   * them wrapped in jQuery objects and cached in variables for later use.
   */
  const searchWidgetContainer = document.getElementById(
    'breed-health-autocomplete-widget'
  );

  if (!searchWidgetContainer) {
    return;
  }

  const $searchBreedBtn = $(
    searchWidgetContainer.getElementsByClassName('view-breed')
  );
  const $searchHealthBtn = $(
    searchWidgetContainer.getElementsByClassName('view-health')
  );
  const $clearHealthBtn = $(
    searchWidgetContainer.getElementsByClassName('clear-health')
  );
  const $clearBreedBtn = $(
    searchWidgetContainer.getElementsByClassName('clear-breed')
  );

  const $breedAutocomplete = $(document.getElementById('breed-autocomplete'));
  const $healthAutocomplete = $(document.getElementById('health-autocomplete'));

  const $breedResults = $(
    searchWidgetContainer.getElementsByClassName('conditions-for-breed')
  );
  const $healthResults = $(
    searchWidgetContainer.getElementsByClassName('conditions-for-health')
  );

  /**
   * Misc. variables
   */
  const identifiableHealth = await $.get(
    `${MYEV_API_BASE}/health-and-breed-list`
  );
  identifiableHealth.push({
    health_id: '000000',
    lay_term: 'Skin & Connective Tissues',
    disorder_name: 'Mast Cell Tumor (MCT)',
    subdisorder_name: '',
    is_recommended_by_embark: true,
    is_recommended_by_ofa: false,
    is_linkage_test: false,
    is_ancestry_based_risk: true,
    breed_display_names:
      'Alapaha Blue Blood Bulldog, , American Bulldog, , American Bully, , American Pit Bull Terrier, , American Staffordshire Terrier, , Boston Terrier, , Boxer, , Bull Terrier, , English Bulldog, , Dogo Argentino, , French Bulldog, , Miniature Bull Terrier, , Olde English Bulldogge, , Staffordshire Bull Terrier, , Staffordshire Terrier',
  });
  const { identifiableBreeds } = await $.get(
    `${MYEV_API_BASE}/breeder-breeds-list`
  );

  $breedAutocomplete.autocomplete({
    source: identifiableBreeds.map((b) => b.display_name),
    create() {
      const $breedAutocompleteWidget = $(this).autocomplete('widget');
      $(this).attr({
        'aria-controls': $breedAutocompleteWidget.attr('id'),
        'aria-autocomplete': 'both',
        'aria-haspopup': 'listbox',
        'aria-expanded': 'false',
      });

      // handle when the autocomplete aria-activedescendant is set
      // on widget and set the autocomplete input to the same value
      $(this).on('autocompletefocus', (_event, _ui) => {
        const $autocompleteWidget = $(this).autocomplete('widget');
        const $autocompleteInput = $(this);
        const id = $autocompleteWidget.attr('aria-activedescendant');

        if (!id) {
          return;
        }

        $autocompleteInput.attr('aria-activedescendant', id);

        // remove the aria-activedescendant from the widget
        $autocompleteWidget.removeAttr('aria-activedescendant');
      });
    },
    open() {
      $(this).attr('aria-expanded', 'true');
    },
    close() {
      $(this).attr('aria-expanded', 'false');
    },
    response() {
      const $breedAutocompleteWidget = $(this).autocomplete('widget');
      $breedAutocompleteWidget.attr({
        'aria-labelledby': 'breed-autocomplete-label',
        role: 'listbox',
      });
      $breedAutocompleteWidget.menu('option', 'role', 'listbox');
    },
  });

  const healthDisplayName = (h) => `${h.disorder_name} ${h.subdisorder_name}`;

  $healthAutocomplete.autocomplete({
    source: identifiableHealth.map(healthDisplayName),
    create() {
      const $healthAutocompleteWidget = $(this).autocomplete('widget');
      $(this).attr({
        'aria-controls': $healthAutocompleteWidget.attr('id'),
        'aria-autocomplete': 'both',
        'aria-haspopup': 'listbox',
        'aria-expanded': 'false',
      });
    },
    open() {
      $(this).attr('aria-expanded', 'true');
    },
    close() {
      $(this).attr('aria-expanded', 'false');
    },
    response() {
      const $healthAutocompleteWidget = $(this).autocomplete('widget');
      $healthAutocompleteWidget.attr({
        'aria-labelledby': 'health-autocomplete-label',
        role: 'listbox',
      });
      $healthAutocompleteWidget.menu('option', 'role', 'listbox');
    },
  });

  function conditionToHtml({
    condition,
    isRecommendedByEmbark,
    isRecommendedByOFA,
  }) {
    const elementId = Math.floor(Math.random() * 100000); // need a unique id for toggling
    const citations = [1, 2, 3, 4, 5]
      .map((i) =>
        condition[`reference_${i}_text`]
          ? `<a target="_blank" class="decorated-link" href="${
              condition[`reference_${i}_link`]
            }">${condition[`reference_${i}_text`]}</a>`
          : ''
      )
      .join('');
    const disclaimerType = getDisclaimerTypeByCondition(condition);
    let disclaimerHeader = '';
    let disclaimerDescription = '';
    if (
      disclaimerType &&
      typeof conditionDisclaimers[disclaimerType] !== 'undefined'
    ) {
      const anchorID = getDisclaimerAnchor(
        disclaimerType,
        condition.health_id,
        'breedSpecificDisease'
      );
      disclaimerHeader = getDisclaimerAnchorLinkText(
        anchorID,
        conditionDisclaimers[disclaimerType].anchorText
      );
      disclaimerDescription = getDisclaimerDescriptionMarkup(
        anchorID,
        conditionDisclaimers[disclaimerType].descriptionMarkup
      );
    }

    return `<tr><td class="condition">
        <h4 class="inline-item">
          ${condition.disorder_name} ${condition.subdisorder_name}
        </h4>
        <div class="inline-item-right">
          ${
            isRecommendedByEmbark
              ? '<img class="icon" src="//front.embarkvet.com/img/icons/small-yellow-dog@2x.png" alt="Embark">'
              : ''
          }
          ${
            isRecommendedByOFA
              ? '<img class="icon ofa-logo" src="//front.embarkvet.com/img/logo/ofa-logo.png" alt="OFA">'
              : ''
          }
      <button
            id="show-more-${elementId}"
           type="button"
           onclick="jQuery('#condition-${elementId}').toggle(500); jQuery('#show-more-${elementId}').attr('aria-expanded', function(index, attr) { return attr === 'true' ? 'false' : 'true'; });"
           class="btn btn--small btn--blue"
           style="display: inline-block;"
           aria-expanded="false"
           aria-controls="condition-${elementId}"
          >Show more…</button>
        </div>
        <div style="display: none;" id="condition-${elementId}">
          ${disclaimerHeader ? '<p>' + disclaimerHeader + '</p>' : ''}
          <small>${condition.lay_term} (${condition.category})</small>
          <ul>
            <li> Gene: ${condition.gene} </li>
            ${
              condition.moi
                ? '<li>Inheritance type: ' + condition.moi + '</li>'
                : ''
            }
          </ul>
          <p>${condition.consumer_brief_description_required}</p>
          ${
            condition.subdisorder_description
              ? '<p>' + condition.subdisorder_description + '</p>'
              : ''
          }
          ${citations ? '<h5>Citations</h5>' + citations : ''}
          ${disclaimerDescription}
        </div>
        </td></tr>
        `;
  }

  const onlyUnique = (value, index, self) => self.indexOf(value) === index;

  function breedsForConditionHtml(conditionDetails, breedDisplayNames) {
    const breeds = breedDisplayNames
      ? breedDisplayNames.split(', ').filter(onlyUnique).sort()
      : ['(None currently known)'];
    const breedList = `<ul class="breeds-for-condition"><li><a class="decorated-link">${breeds.join(
      '</li><li>'
    )}</li></ul>`;
    return `<div class="condition">
        <table><tbody>${conditionToHtml(conditionDetails)}</tbody></table>
        <p>Seen in breeds:</p>
        ${breedList}
        </div>`;
  }

  /********************
   *  Search Handlers *
   ********************/

  async function doBreedSearch() {
    const breedDisplayName = $breedAutocomplete.val();

    if (!breedDisplayName) {
      return false; // Exit early if there is no search value.
    }

    let commonDiseases = await $.get(
      `${MYEV_API_BASE}/bcd/display/${encodeURIComponent(breedDisplayName)}`
    );

    const localHealthForBreed = getLocalHealthCopiesByBreed(breedDisplayName);
    if (localHealthForBreed.length) {
      commonDiseases = [...commonDiseases, ...localHealthForBreed];
    }

    const searchType = 'Breed Condition Search - Shopify';
    const eventProperties = {
      eventCategory: searchType,
      eventAction: breedDisplayName,
      eventURL: window.location.href,
    };

    if (commonDiseases.length) {
      pushGTMCustomEvent(searchType, eventProperties);
    } else {
      eventProperties.eventAction += ' - no results';
      pushGTMCustomEvent(searchType, eventProperties);
    }

    const html = commonDiseases.length
      ? `<h2>Conditions for: ${breedDisplayName}</h2><table class="results-table"><tbody>` +
        commonDiseases.map(conditionToHtml).join('') +
        '</tbody></table>'
      : `<h4>No conditions found for ${breedDisplayName}. However, you will
          still receive results for all health conditions on <a
          href="https://embarkvet.com/health-list">our health list</a>.
          Please contact <a href="mailto:breeders@embarkvet.com">breeders@embarkvet.com</a>
          if you have questions.</h4>`;
    $breedResults.html(html);
    $clearBreedBtn.show();
    $breedResults.show();
  }

  async function doHealthSearch() {
    const selectedHealthDisplayName = $healthAutocomplete.val();

    const searchType = 'Health Condition Search';
    const eventProperties = {
      eventCategory: searchType,
      eventAction: selectedHealthDisplayName,
      eventURL: window.location.href,
    };

    if (!selectedHealthDisplayName) {
      eventProperties.eventAction += ' - no results';
      pushGTMCustomEvent(searchType, eventProperties);
      return false; // Exit early if there is no search value.
    }

    const healthCondition = identifiableHealth.filter(
      (h) => healthDisplayName(h) === selectedHealthDisplayName
    );

    if (healthCondition.length) {
      pushGTMCustomEvent(searchType, eventProperties);
      const hc = healthCondition[0];
      let conditionDetails;
      if (isLocalCopyAvailable(hc.health_id)) {
        conditionDetails = getLocalCopy(hc.health_id);
      } else {
        conditionDetails = await $.get(
          `${MYEV_API_BASE}/health-conditions/${hc.health_id}`
        );
      }
      $healthResults.html(
        breedsForConditionHtml(conditionDetails, hc.breed_display_names)
      );
      $('.breeds-for-condition li a').click((e) => {
        const breedName = e.target.innerText;
        $breedAutocomplete.val(breedName);
        $searchBreedBtn.click();
      });
      $clearHealthBtn.show();
      $healthResults.show();
    }
  }

  /****************************************
   * Search event listeners and callbacks *
   ****************************************/

  // Run search when on screen search button is clicked.
  $searchBreedBtn.click(doBreedSearch);
  $searchHealthBtn.click(doHealthSearch);

  // Run search when keyboard enter key is pressed.
  $breedAutocomplete.keydown((e) => {
    if (e.keyCode === 13) {
      doBreedSearch().catch(notifyHandledException);
    }
  });
  $healthAutocomplete.keydown((e) => {
    if (e.keyCode === 13) {
      doHealthSearch().catch(notifyHandledException);
    }
  });

  /************************
   * Clear search results *
   ************************/

  $clearBreedBtn.click(() => {
    $breedResults.hide();
    $clearBreedBtn.hide();
  });
  $clearHealthBtn.click(() => {
    $healthResults.hide();
    $clearHealthBtn.hide();
  });
}
